import React from 'react';

const C = {
  ink: '#344767', muted: '#8392ab', border: '#e9ecef',
  primary: '#5e72e4', success: '#2dce89', warning: '#fb6340',
  bgSoft: '#f8f9fe', shadow: '0 2px 24px rgba(52,71,103,0.09)',
};

const features = [
  { icon: 'fas fa-funnel-dollar', color: '#5e72e4', title: 'Lead Pipeline', desc: 'Visual Kanban board to track every lead from first contact to deal closed.' },
  { icon: 'fas fa-bell', color: '#fb6340', title: 'Smart Follow-Ups', desc: 'Automated reminders ensure no lead ever falls through the cracks.' },
  { icon: 'fas fa-chart-bar', color: '#2dce89', title: 'Advanced Reports', desc: 'Source performance, conversion funnels, and employee leaderboards at a glance.' },
  { icon: 'fas fa-users', color: '#f5365c', title: 'Team Management', desc: 'Assign leads, track employee performance, and manage roles with ease.' },
  { icon: 'fas fa-comment-dots', color: '#11cdef', title: 'Multi-Channel', desc: 'Capture leads from Facebook, Instagram, 99acres, Google Ads, and more.' },
  { icon: 'fas fa-building', color: '#825ee4', title: 'Multi-Tenant', desc: 'One platform, multiple agencies. Each client gets their own isolated workspace.' },
];

const stats = [
  { value: '10,000+', label: 'Leads Managed' },
  { value: '500+', label: 'Sales Agents' },
  { value: '50+', label: 'Agency Clients' },
  { value: '99.9%', label: 'Uptime' },
];

const team = [
  { name: 'Product Team', role: 'Design & Engineering', icon: 'fas fa-laptop-code', color: '#5e72e4' },
  { name: 'Support Team', role: '24/7 Client Success', icon: 'fas fa-headset', color: '#2dce89' },
  { name: 'Sales Team', role: 'Onboarding & Growth', icon: 'fas fa-handshake', color: '#fb6340' },
];

export default function About() {
  return (
    <div style={{ background: C.bgSoft, minHeight: '100vh', fontFamily: 'inherit' }}>

      {/* ── hero ── */}
      <div style={{ background: 'linear-gradient(135deg,#344767 0%,#21243d 100%)', padding: '60px 24px 80px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, borderRadius: 20, background: 'rgba(255,255,255,0.12)', fontSize: 30, color: '#fff', marginBottom: 20 }}>
          <i className="fas fa-rocket" />
        </div>
        <h1 style={{ margin: '0 0 14px', fontSize: 36, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
          Next Follow Up
        </h1>
        <p style={{ margin: '0 auto', maxWidth: 560, fontSize: 17, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
          The all-in-one Real Estate CRM built for modern sales teams — capture, nurture, and close more deals, faster.
        </p>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px 60px' }}>

        {/* ── stats row ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: -32, marginBottom: 48, justifyContent: 'center' }}>
          {stats.map(({ value, label }) => (
            <div key={label} style={{ background: '#fff', borderRadius: 16, boxShadow: C.shadow, padding: '22px 32px', textAlign: 'center', minWidth: 130, flex: '1 1 120px' }}>
              <p style={{ margin: 0, fontSize: 28, fontWeight: 800, color: C.primary }}>{value}</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
            </div>
          ))}
        </div>

        {/* ── mission ── */}
        <div style={{ background: '#fff', borderRadius: 20, boxShadow: C.shadow, padding: '36px 40px', marginBottom: 32, display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `${C.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: C.primary, flexShrink: 0 }}>
            <i className="fas fa-bullseye" />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: C.ink }}>Our Mission</h2>
            <p style={{ margin: 0, fontSize: 15, color: C.muted, lineHeight: 1.7 }}>
              We built Next Follow Up because we saw too many real estate deals lost not to better competitors — but to forgotten callbacks, missed follow-ups, and data scattered across WhatsApp, Excel, and sticky notes.
              Our mission is simple: give every sales team the tools to respond faster, follow up smarter, and close more deals — without the chaos.
            </p>
          </div>
        </div>

        {/* ── features grid ── */}
        <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 800, color: C.ink }}>What We Offer</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16, marginBottom: 40 }}>
          {features.map(({ icon, color, title, desc }) => (
            <div key={title} style={{ background: '#fff', borderRadius: 16, boxShadow: C.shadow, padding: '24px 26px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color, flexShrink: 0 }}>
                <i className={icon} />
              </div>
              <div>
                <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 800, color: C.ink }}>{title}</h3>
                <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── team ── */}
        <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 800, color: C.ink }}>The Team Behind It</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 40 }}>
          {team.map(({ name, role, icon, color }) => (
            <div key={name} style={{ background: '#fff', borderRadius: 16, boxShadow: C.shadow, padding: '22px 26px', display: 'flex', gap: 14, alignItems: 'center', flex: '1 1 220px' }}>
              <div style={{ width: 48, height: 48, borderRadius: 13, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color, flexShrink: 0 }}>
                <i className={icon} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: C.ink }}>{name}</p>
                <p style={{ margin: 0, fontSize: 12, color: C.muted }}>{role}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── contact CTA ── */}
        <div style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)', borderRadius: 20, padding: '36px 40px', textAlign: 'center' }}>
          <i className="fas fa-envelope-open-text" style={{ fontSize: 32, color: '#fff', marginBottom: 14, display: 'block' }} />
          <h3 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: '#fff' }}>Get in Touch</h3>
          <p style={{ margin: '0 0 22px', fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>Have questions, want a demo, or need support? We're here for you.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:support@nextfollowup.in"
              style={{ background: '#fff', color: C.primary, borderRadius: 10, padding: '10px 22px', fontWeight: 700, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-envelope" /> support@nextfollowup.in
            </a>
            <a href="/quicksupport"
              style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 10, padding: '10px 22px', fontWeight: 700, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-headset" /> Quick Support
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
